import {useCallback, useEffect} from 'react';
import {EmailSubscriptionsServiceClientImpl} from '../../../grpc_client/grips/protos/email_subscriptions/v1/email_subscriptions_service';
import {
  useGripsClientContext,
  useGrpcClientChannel,
} from '../../../ui/sharedContexts/GrpcClientContext';
import {useUserSessionContext} from '../../../ui/sharedContexts/UserSessionContext';
import {useDevice} from '../../../utils/hooks/useDevice';
import {useMemoizedFunction} from '../../../utils/hooks/useMemoizedFunction';
import {Log} from '../../../utils/logger/log';
import {useDropsSubscriptionsContext} from '../context/SubscriptionsProvider';

export const useIndividualDropSubscriptionStatus = (
  dropId: number | undefined,
) => {
  const channel = useGrpcClientChannel();
  const {childShopId, shopId} = useGripsClientContext();
  const {device} = useDevice();
  const {ayUser, guest, updateIndividualDrops} = useDropsSubscriptionsContext();
  const {email} = useUserSessionContext();

  const updateIndividualDropsMemoized = useMemoizedFunction(
    updateIndividualDrops,
  );

  const getEmailsForStatusCheck = useCallback(() => {
    if (email) {
      return [email].concat(
        Object.entries(ayUser?.customEmails || {})
          .filter(customEmailEntry =>
            customEmailEntry[1].includes(Number(dropId)),
          )
          .map(entry => entry[0]),
      );
    } else if (guest?.email) {
      return [guest?.email];
    }

    return [];
  }, [email, ayUser?.customEmails, guest?.email, dropId]);

  useEffect(() => {
    if (!email && !guest?.email) {
      return undefined;
    }

    if (!dropId) {
      return undefined;
    }

    const emails = getEmailsForStatusCheck();

    const promises = [];

    const client = new EmailSubscriptionsServiceClientImpl(channel);

    for (const customEmail of emails) {
      promises.push(
        client.getDropSubscriptionStatus({
          config: {
            childShopId,
            shopId,
            device,
          },
          email: customEmail,
          dropId,
        }),
      );
    }

    Promise.all(promises)
      .then(datas => {
        const atLeastOneSubscribed = datas.some(data => data.isEmailEnabled);
        updateIndividualDropsMemoized.current?.({
          [email ? 'ayUser' : 'guest']: {
            [dropId]: atLeastOneSubscribed,
          },
        });
      })
      .catch(_error => {
        Log.error('Error in get drop subscription status service', {
          error: _error,
          message: _error.message,
          statusCode: _error.code,
        });
      });
  }, [
    channel,
    childShopId,
    device,
    shopId,
    email,
    dropId,
    guest,
    updateIndividualDropsMemoized,
  ]);
};
